import React, { useRef, useContext, useEffect, useState } from 'react';
import { Observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import List, { ItemDragging } from 'devextreme-react/list';
import DataSource from 'devextreme/data/data_source';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { toast } from 'react-toastify';

import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';

import BookingTypeUpdateStore from '../../../../stores/BookingTypeUpdateStore';
import AuthStore from '../../../../stores/AuthStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as bh from '../../../../utilities/badgeHelper';
import * as oh from '../../../../utilities/operationHelper';

import './ManageBookingTypes.scss';

function ManageBookingTypes(props) {
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const bookingTypeUpdate = useContext(BookingTypeUpdateStore);
    const auth = useContext(AuthStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [sortEnabled, setSortEnabled] = useState(false);
    const [originalPublicBookingTypes, setOriginalPublicBookingTypes] = useState(null);

    useEffect(() => {
        return (() => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        })
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (bookingTypeUpdate.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        bookingTypeUpdate.sort()
            .then(() => {
                setSortEnabled(false);
                toast.dark(() => <p>Booking Types Updated.</p>);
            })
    }

    const handleSortEnable = (event) => {
        setOriginalPublicBookingTypes([...toJS(bookingTypeUpdate.publicBookingTypes)]);
        setSortEnabled(true);
    }

    const handleSortCancel = (event) => {
        bookingTypeUpdate.data = originalPublicBookingTypes;
        setOriginalPublicBookingTypes(null);
        setSortEnabled(false);
    }

    const handleBookingTypeReorder = (item) => {
        const { toIndex, fromIndex } = item;
        let PublicBookingTypes = [...toJS(bookingTypeUpdate.publicBookingTypes)];

        PublicBookingTypes.splice(fromIndex, 1);
        PublicBookingTypes.splice(toIndex, 0, toJS(bookingTypeUpdate.publicBookingTypes[fromIndex]));

        for (let i = 0; i < PublicBookingTypes.length; i++) {
            PublicBookingTypes[i].displayOrder = (i + 1);
        }

        for (let i = 0; i < PublicBookingTypes.length; i++) {
            const ii = bookingTypeUpdate.data.findIndex(d => d.id === PublicBookingTypes[i].id);
            if (ii >= 0) {
                bookingTypeUpdate.data[ii].displayOrder = PublicBookingTypes[i].displayOrder;
            }
        }

        bookingTypeUpdate.data = bookingTypeUpdate.data.slice().sort((a, b) => a.displayOrder - b.displayOrder);
        bookingTypeUpdate.hasUnsavedChanges = true;
    }

    const handleBookingTypeAdd = (event) => {
        bookingTypeUpdate.clearNewBookingType();

        quickDrawer.activateQuickDrawer('online-booking-type', 'create', null, handleBookingTypeAddSuccess, handleBookingTypeAddCancel)
            .then(drawer => {
                if (isMounted.current) {
                    focusTimer.current = setTimeout(() => {
                        quickDrawerFocus(drawer);
                    }, 100);
                }
            });
    }

    const handleBookingTypeAddSuccess = () => {
        toast.dark(() => <p>Booking Type Created.</p>);
    }

    const handleBookingTypeAddCancel = () => {
        bookingTypeUpdate.clearNewBookingType();
    }

    const handleBookingTypeUpdate = (event, id) => {
        bookingTypeUpdate.selectedBookingTypeId = id;

        quickDrawer.activateQuickDrawer('online-booking-type', 'update', null, handleBookingTypeUpdateSuccess, handleBookingTypeUpdateCancel)
            .then(drawer => {
                if (isMounted.current) {
                    focusTimer.current = setTimeout(() => {
                        quickDrawerFocus(drawer);
                    }, 100);
                }
            });
    }

    const handleBookingTypeUpdateSuccess = () => {
        toast.dark(() => <p>Booking Type Updated.</p>);
    }

    const handleBookingTypeUpdateCancel = () => {
        bookingTypeUpdate.clearNewBookingType();
    }

    const renderBookingType = (item) => {
        return <div className='form-group mb-0 py-1'>
            <div
                className={'booking-type' + (!sortEnabled ? ' update' : ' sort')}
            >
                <div className='mb-1'>{bh.renderBookingType(item)}</div>
                <h4
                    className='booking-type-name d-block mb-1 text-truncate'
                    onClick={!sortEnabled ? e => { handleBookingTypeUpdate(e, item.id) } : null}
                >
                    <strong>{item.name}</strong>
                </h4>
                <code
                    className='booking-type-url d-block fs-75 text-truncate'
                    onClick={(e) => { handleCopyUrl(e, `${auth.currentTenant.onlineBookingUrl}_ext/${item.id}`) }}
                >
                    {`${auth.currentTenant.onlineBookingUrl}_ext/${item.id}`}
                </code>
            </div>
        </div>
    }

    const handleCopyUrl = (event, url) => {
        navigator.clipboard.writeText(url).then(() => {
            toast.dark(() => <p>Copied to clipboard.</p>);
        })
    }

    return <>
        <Observer>{() =>
            <>
                {
                    (props.drawer === quickDrawer.drawerOpened) ?
                        <GlobalHotKeys
                            keyMap={{
                                close: ['esc'],
                            }}
                            handlers={{
                                close: event => {
                                    handleCancel(event)
                                },
                            }}
                            allowChanges={true}
                        /> : null
                }
            </>
        }</Observer>
        <Observer>{() =>
            <form onSubmit={handleSubmit}>
                <fieldset disabled={bookingTypeUpdate.isSaving}>
                    <div className='quick-drawer'>
                        <QuickDrawerHeader
                            drawer={props.drawer}
                            icon={oh.getIcon('appointment', 'update')}
                            action='Manage'
                            category='Online Booking Types'
                            onCancel={handleCancel}
                        />
                        <div className='quick-drawer-body'>
                            <div className='manage-booking-types-page body-content'>
                                <Observer>{() =>
                                    <>
                                        {
                                            bookingTypeUpdate.isReady ?
                                                <FadeIn>
                                                    <section>
                                                        <div className='row datasets'>
                                                            <div className='col-12'>
                                                                <div className='d-flex w-100 align-items-center'>
                                                                    <strong className='text-gray-600 text-uppercase flex-1 m-0 mr-2'>Online Booking Page</strong>
                                                                    <button
                                                                        type='button'
                                                                        className={'btn btn-link p-0' + (sortEnabled ? ' d-none' : '')}
                                                                        onClick={handleSortEnable}
                                                                    >
                                                                        Change display order
                                                                    </button>
                                                                </div>
                                                                <div>
                                                                    <List
                                                                        dataSource={
                                                                            new DataSource({
                                                                                key: "id",
                                                                                store: bookingTypeUpdate.publicBookingTypes,
                                                                                paginate: false,
                                                                            })
                                                                        }
                                                                        activeStateEnabled={false}
                                                                        focusStateEnabled={false}
                                                                        hoverStateEnabled={false}
                                                                        itemRender={(data) => { return renderBookingType(data) }}
                                                                    >
                                                                        {
                                                                            sortEnabled ?
                                                                                <ItemDragging
                                                                                    allowReordering={true}
                                                                                    onReorder={handleBookingTypeReorder}
                                                                                >
                                                                                </ItemDragging> : null
                                                                        }
                                                                    </List>
                                                                </div>
                                                                {
                                                                    bookingTypeUpdate.privateBookingTypes && bookingTypeUpdate.privateBookingTypes.length > 0 ?
                                                                        <>
                                                                            <hr />
                                                                            <div className='d-flex w-100 align-items-center'>
                                                                                <strong className='text-gray-600 text-uppercase flex-1 m-0 mr-2'>Direct Link Access only</strong>
                                                                            </div>
                                                                            <div>
                                                                                <List
                                                                                    dataSource={
                                                                                        new DataSource({
                                                                                            key: "id",
                                                                                            store: bookingTypeUpdate.privateBookingTypes,
                                                                                            paginate: false,
                                                                                        })
                                                                                    }
                                                                                    activeStateEnabled={false}
                                                                                    focusStateEnabled={false}
                                                                                    hoverStateEnabled={false}
                                                                                    itemRender={(data) => { return renderBookingType(data) }}
                                                                                >
                                                                                </List>
                                                                            </div>
                                                                        </> : null
                                                                }
                                                            </div>
                                                        </div>
                                                    </section>
                                                </FadeIn> : renderQuickDrawerLoading()
                                        }
                                    </>
                                }</Observer>
                            </div>
                        </div>
                        <div className='quick-drawer-action'>
                            <div className='row'>
                                <div className='col-4'>
                                    {
                                        !sortEnabled ?
                                            <button
                                                type='button'
                                                className='btn btn-light'
                                                onClick={handleBookingTypeAdd}
                                            >
                                                Add
                                            </button> : null
                                    }
                                </div>
                                <div className='col-8'>
                                    {
                                        sortEnabled ?
                                            <div className='float-right'>
                                                <button
                                                    type='button'
                                                    className='btn btn-link btn-cancel mr-2'
                                                    onClick={handleSortCancel}
                                                >Cancel</button>
                                                <button
                                                    type='submit'
                                                    className='btn btn-primary'
                                                >Update</button>
                                            </div> :
                                            <div className='float-right'>
                                                <button
                                                    type='button'
                                                    className='btn btn-link btn-cancel'
                                                    onClick={handleCancel}
                                                >Cancel</button>
                                            </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
            </form>
        }</Observer>
    </>
}

export default ManageBookingTypes;